import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Monitor,
  Layers,
  FolderTree,
  FileText,
  FolderArchive,
  Activity,
  Building2,
  Users,
  Bell,
  Settings,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  User,
  ScrollText,
  Key,
  KeyRound,
  Terminal,
  BarChart3,
  Globe,
  Shield,
  LogOut,
  Cpu,
  LifeBuoy,
} from "lucide-react";
import { HelpButton } from "../help/HelpButton";
import {
  getActingCompanyName,
  getActingTenantId,
  getRole,
  getTenantId,
  isActingAsCompany,
  stopActingAsCompany,
} from "../auth/session";
import { signOut } from "../auth/cognito";
import { Breadcrumbs } from "./Breadcrumbs";
import { useTheme } from "../hooks/useTheme";

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  minRole: string;
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

const iconSize = 18;

const navSections: NavSection[] = [
  {
    items: [
      { label: "Dashboard", path: "/", icon: <LayoutDashboard size={iconSize} />, minRole: "viewer" },
      { label: "Devices", path: "/devices", icon: <Monitor size={iconSize} />, minRole: "viewer" },
      { label: "Device groups", path: "/groups", icon: <FolderTree size={iconSize} />, minRole: "viewer" },
      { label: "Device logs", path: "/device-logs", icon: <Activity size={iconSize} />, minRole: "viewer" },
      { label: "Tasks", path: "/tasks", icon: <Layers size={iconSize} />, minRole: "viewer" },
      { label: "Firmware", path: "/firmware", icon: <FileText size={iconSize} />, minRole: "viewer" },
      { label: "Resources", path: "/resources", icon: <FolderArchive size={iconSize} />, minRole: "viewer" },
    ],
  },
  {
    label: "Management",
    items: [
      { label: "Company", path: "/settings", icon: <Building2 size={iconSize} />, minRole: "tenant_admin" },
      { label: "Group Keys", path: "/settings/group-keys", icon: <Key size={iconSize} />, minRole: "tenant_admin" },
      { label: "API Credentials", path: "/settings/api-credentials", icon: <Terminal size={iconSize} />, minRole: "tenant_admin" },
      { label: "Usage", path: "/settings/usage", icon: <BarChart3 size={iconSize} />, minRole: "tenant_admin" },
      { label: "Users", path: "/users", icon: <Users size={iconSize} />, minRole: "tenant_admin" },
      { label: "Audit Log", path: "/audit-log", icon: <ScrollText size={iconSize} />, minRole: "tenant_admin" },
      { label: "Notifications", path: "/notifications", icon: <Bell size={iconSize} />, minRole: "viewer" },
      { label: "Profile", path: "/profile", icon: <User size={iconSize} />, minRole: "viewer" },
      { label: "Passkeys", path: "/settings/passkeys", icon: <KeyRound size={iconSize} />, minRole: "viewer" },
    ],
  },
  {
    label: "Platform Admin",
    items: [
      { label: "Admin Dashboard", path: "/admin/dashboard", icon: <Globe size={iconSize} />, minRole: "super_admin" },
      { label: "All Devices", path: "/admin/devices", icon: <Monitor size={iconSize} />, minRole: "super_admin" },
      { label: "Companies", path: "/admin/companies", icon: <Building2 size={iconSize} />, minRole: "super_admin" },
      { label: "Device Types", path: "/admin/device-types", icon: <Cpu size={iconSize} />, minRole: "super_admin" },
      { label: "All Users", path: "/admin/users", icon: <Shield size={iconSize} />, minRole: "super_admin" },
    ],
  },
  {
    label: "Support",
    items: [
      { label: "Help & Guides", path: "/help", icon: <LifeBuoy size={iconSize} />, minRole: "viewer" },
    ],
  },
];

const roleRank: Record<string, number> = {
  viewer: 0,
  operator: 1,
  tenant_admin: 2,
  super_admin: 3,
};

function hasMinRole(minRole: string): boolean {
  const current = getRole() ?? "viewer";
  return (roleRank[current] ?? 0) >= (roleRank[minRole] ?? 0);
}

export function Shell() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [, setActingContextVersion] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenantId = getTenantId();
  const actingTenantId = getActingTenantId();
  const actingCompanyName = getActingCompanyName();
  const actingCompany = isActingAsCompany();
  const isSuperAdmin = hasMinRole("super_admin");
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    if (!isSuperAdmin || !location.pathname.startsWith("/admin") || !isActingAsCompany()) {
      return;
    }

    stopActingAsCompany();
    queryClient.clear();
    setActingContextVersion((version) => version + 1);
  }, [isSuperAdmin, location.pathname, queryClient]);

  function isActive(path: string) {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  }

  async function handleLogout(): Promise<void> {
    if (loggingOut) {
      return;
    }

    setLoggingOut(true);

    try {
      await signOut();
      queryClient.clear();
      navigate("/sign-in", { replace: true });
    } finally {
      setMobileOpen(false);
      setLoggingOut(false);
    }
  }

  function handleExitCompanyView(): void {
    stopActingAsCompany();
    queryClient.clear();
    setMobileOpen(false);
    navigate("/admin/companies");
  }

  const desktopSidebarWidth = collapsed ? "md:w-16" : "md:w-52";
  const mobileSidebarState = mobileOpen ? "translate-x-0" : "-translate-x-full";

  return (
    <div className="flex h-screen bg-riot-navy-50 dark:bg-riot-navy-950">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-20 bg-black/40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar — white in light mode, navy in dark mode */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-30 flex w-52 shrink-0 flex-col border-r border-riot-navy-100 bg-white
          dark:border-riot-navy-700 dark:bg-riot-navy-900
          transition-all duration-200
          ${mobileSidebarState}
          md:static md:translate-x-0 ${desktopSidebarWidth}
        `}
      >
        {/* Logo */}
        <div className="flex h-12 items-center gap-2.5 border-b border-riot-navy-100 px-4 dark:border-riot-navy-700">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-riot-navy-800">
            <Monitor size={14} className="text-riot-gold-500" />
          </div>
          {!collapsed && <span className="text-sm font-bold text-riot-navy-900 dark:text-white">RIoT-FOTA</span>}
          <button
            onClick={() => setMobileOpen(false)}
            className="ml-auto rounded p-1 text-riot-navy-400 hover:text-riot-navy-700 dark:hover:text-white md:hidden"
            aria-label="Close sidebar"
          >
            <X size={16} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-1">
          {navSections.map((section, si) => {
            const visible = section.items.filter((item) => {
              if (!hasMinRole(item.minRole)) return false;
              return !(isSuperAdmin && !actingCompany && item.path === "/admin/devices");
            });
            if (visible.length === 0) return null;
            return (
              <div key={si}>
                {si > 0 && <div className="mx-3 my-2 border-t border-riot-navy-100 dark:border-riot-navy-700" />}
                {section.label && !collapsed && (
                  <p className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-riot-navy-400">
                    {section.label}
                  </p>
                )}
                {visible.map((item) => {
                  const resolvedItem = isSuperAdmin && !actingCompany && item.path === "/devices"
                    ? { ...item, label: "All Devices", path: "/admin/devices" }
                    : item;
                  const active = isActive(resolvedItem.path);
                  return (
                    <Link
                      key={`${item.path}:${resolvedItem.path}`}
                      to={resolvedItem.path}
                      onClick={() => setMobileOpen(false)}
                      title={collapsed ? resolvedItem.label : undefined}
                      className={`
                        mx-2 my-0.5 flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px]
                        transition-colors
                        ${active
                          ? "bg-riot-gold-50 font-medium text-riot-navy-900 dark:bg-riot-navy-700 dark:text-riot-gold-400"
                          : "text-riot-navy-600 hover:bg-riot-navy-50 hover:text-riot-navy-900 dark:text-riot-navy-200 dark:hover:bg-riot-navy-800 dark:hover:text-white"}
                        ${collapsed ? "justify-center" : ""}
                      `}
                    >
                      <span className={active ? "text-riot-gold-600 dark:text-riot-gold-400" : "text-riot-navy-400"}>
                        {resolvedItem.icon}
                      </span>
                      {!collapsed && resolvedItem.label}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Tenant info */}
        {!collapsed && tenantId && (
          <div className="border-t border-riot-navy-100 px-4 py-2.5 text-[11px] text-riot-navy-400 dark:border-riot-navy-700">
            Tenant: {tenantId.slice(0, 8)}...
          </div>
        )}

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden items-center gap-2 border-t border-riot-navy-100 px-4 py-2.5 text-xs text-riot-navy-400 hover:bg-riot-navy-50 hover:text-riot-navy-700 dark:border-riot-navy-700 dark:hover:bg-riot-navy-800 dark:hover:text-white md:flex"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          {!collapsed && "Collapse menu"}
        </button>
      </aside>

      {/* Main area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex h-12 items-center justify-between border-b border-riot-navy-100 bg-white px-4 dark:border-riot-navy-700 dark:bg-riot-navy-900 md:px-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="rounded p-1 text-riot-navy-500 hover:bg-riot-navy-50 md:hidden"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
            <Breadcrumbs />
          </div>
          <div className="flex items-center gap-1">
            {hasMinRole("super_admin") && (
              <Link
                to="/admin/companies"
                className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-riot-navy-500 hover:bg-riot-navy-50 hover:text-riot-navy-700"
              >
                <Building2 size={15} />
                <span className="hidden sm:inline">All companies</span>
              </Link>
            )}
            <button className="relative flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-riot-navy-500 hover:bg-riot-navy-50 hover:text-riot-navy-700">
              <Bell size={15} />
              <span className="hidden sm:inline">Notifications</span>
            </button>
            <HelpButton />
            <button
              onClick={toggleTheme}
              className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-riot-navy-500 hover:bg-riot-navy-50 hover:text-riot-navy-700 dark:text-riot-navy-300 dark:hover:bg-riot-navy-800"
              aria-label="Toggle dark mode"
            >
              {theme === "dark" ? (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
              ) : (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
              )}
            </button>
            <Link
              to="/settings"
              className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-riot-navy-500 hover:bg-riot-navy-50 hover:text-riot-navy-700 dark:text-riot-navy-300 dark:hover:bg-riot-navy-800"
            >
              <Settings size={15} />
              <span className="hidden sm:inline">Settings</span>
            </Link>
            <button
              onClick={() => void handleLogout()}
              disabled={loggingOut}
              className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-riot-navy-500 hover:bg-riot-navy-50 hover:text-riot-navy-700 disabled:cursor-not-allowed disabled:opacity-60 dark:text-riot-navy-300 dark:hover:bg-riot-navy-800"
              aria-label="Log out"
            >
              <LogOut size={15} />
              <span className="hidden sm:inline">{loggingOut ? "Signing out..." : "Log out"}</span>
            </button>
          </div>
        </header>

        {actingCompany && actingTenantId && (
          <div className="border-b border-riot-gold-200 bg-riot-gold-50/90 px-4 py-2.5 dark:border-riot-gold-500/20 dark:bg-riot-gold-500/10 md:px-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-riot-gold-700 dark:text-riot-gold-400">
                  Company view active
                </p>
                <p className="text-sm text-riot-navy-700 dark:text-riot-navy-100">
                  {actingCompanyName || actingTenantId} is the active tenant context for tenant pages.
                </p>
              </div>
              <button
                onClick={handleExitCompanyView}
                className="inline-flex items-center justify-center rounded-md border border-riot-gold-300 px-3 py-1.5 text-xs font-medium text-riot-gold-800 hover:bg-riot-gold-100 dark:border-riot-gold-500/30 dark:text-riot-gold-300 dark:hover:bg-riot-gold-500/10"
              >
                Exit company view
              </button>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
